import { CategorySettings } from '@repo/shared/dtos/organization'
import { Button } from '@repo/ui/button'
import {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@repo/ui/card'
import { useT } from '@repo/ui/hooks'
import { Input } from '@repo/ui/input'
import { onInput } from '@repo/ui/utils'
import { debounce, isEqual } from 'lodash'
import { PlusIcon, Trash2Icon } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'

import { ResetAndUpdate } from '@/components/ResetAndUpdate'
import { adminStoreActions } from '@/store/actions'
import { useStoreDispatch } from '@/store/hooks'
import { Organization } from '@/store/user'

export function CategoriesRoute({ organization }: { organization: Organization }) {
  const { tc, t } = useT({ keyPrefix: 'admin.categories' })
  const dispatch = useStoreDispatch()

  const initialCategories = organization.categoriesSettings
  const [categories, setCategories] = useState<CategorySettings[]>(initialCategories)
  useEffect(() => {
    setCategories(initialCategories)
  }, [initialCategories])

  const hasChanges = !isEqual(initialCategories, categories)

  const handleUpdate = () => {
    const categoriesSettings = Array.from(
      new Set(
        categories
          .map((c) => c.name.trim())
          .filter((c) => c.length > 0)
          .map((c) => c.toLocaleUpperCase()),
      ),
    )
      .sort()
      .map((c) => ({ name: c }))

    void dispatch(
      adminStoreActions.organization.update({
        fields: { categoriesSettings },
      }),
    )
  }
  const handleReset = () => {
    setCategories(initialCategories)
  }
  const handleAdd = () => {
    setCategories((prev) => [...prev, { name: '' }])
  }
  const handleChange = (oldName: string, newName: string) => {
    setCategories((prev) =>
      prev.map((category) =>
        category.name === oldName ? { ...category, name: newName } : category,
      ),
    )
  }
  const handleDelete = (name: string) => {
    setCategories((prev) => prev.filter((category) => category.name !== name))
  }
  return (
    <Card className="flex h-full w-full flex-col">
      <CardHeader>
        <CardTitle>{tc('category', { cfl: true, count: categories.length })}</CardTitle>
        <CardDescription>{t('description')}</CardDescription>
        <CardAction>
          <Button variant="outline" className="mr-2" onClick={handleAdd}>
            <PlusIcon className="size-4" />
            {tc('add', { cfl: true })}
          </Button>
          <ResetAndUpdate disabled={!hasChanges} onReset={handleReset} onUpdate={handleUpdate} />
        </CardAction>
      </CardHeader>
      <CardContent>
        <div className="inline-flex flex-wrap gap-4">
          {categories.map((category, i) => (
            <Category
              key={i}
              category={category}
              onChange={handleChange}
              onDelete={() => handleDelete(category.name)}
            />
          ))}
        </div>
      </CardContent>
    </Card>
  )
}

function Category({
  category,
  onChange,
  onDelete,
}: {
  category: CategorySettings
  onChange: (oldName: string, newName: string) => void
  onDelete: () => void
}) {
  const [value, setValue] = useState(category.name)
  useEffect(() => {
    setValue(category.name)
  }, [category.name])

  const debounceOnChange = useMemo(
    () => debounce((newValue: string) => onChange(category.name, newValue), 300),
    [onChange, category.name],
  )

  const handleChange = (newValue: string) => {
    // Only allow alphabets and numbers and underscores and dashes and whitespaces
    if (/^[A-Za-z0-9_\s-]*$/.test(newValue)) {
      setValue(newValue)
      debounceOnChange(newValue)
    }
  }
  return (
    <div className="flex gap-1">
      <Input
        value={value}
        autoFocus={category.name === ''}
        className="uppercase"
        onChange={onInput(handleChange)}
      />
      <Button size="icon" variant="outline" className="flex-shrink-0" onClick={onDelete}>
        <Trash2Icon className="size-4" />
      </Button>
    </div>
  )
}
